import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import { format, isSameDay, isValid, parse } from "date-fns";
import { Box, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import "react-datepicker/dist/react-datepicker.css";
import "./dueDatePicker.css";
import { AH_INPUT_BORDER, AH_MUTED, AH_TEXT, AH_WHITE } from "./theme";

const toDate = (value) => {
  if (!value) return null;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
};

const DueDateInput = forwardRef(function DueDateInput(
  { id, value, onClick, onClear, placeholder, label = "Due Date", size, disabled },
  ref
) {
  return (
    <TextField
      id={id}
      label={label}
      size={size}
      placeholder={placeholder || "mm/dd/yyyy"}
      value={value || ""}
      onClick={onClick}
      inputRef={ref}
      disabled={disabled}
      fullWidth
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            {value && !disabled ? (
              <IconButton
                size="small"
                aria-label="Clear due date"
                edge="end"
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onClear();
                }}
                sx={{ color: AH_MUTED, mr: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
            <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: AH_MUTED }} />
          </InputAdornment>
        ),
      }}
      inputProps={{ "aria-label": "Due Date" }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          fontSize: "0.875rem",
          backgroundColor: AH_WHITE,
          cursor: "pointer",
          "& fieldset": { borderColor: AH_INPUT_BORDER },
          "&:hover fieldset": { borderColor: "#cbd5e1" },
          "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
        },
        "& .MuiInputBase-input": { cursor: "pointer" },
        "& .MuiInputLabel-root": { fontSize: "0.875rem" },
      }}
    />
  );
});

const CalendarHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5, pt: 0.5, pb: 0.25 }}>
    <IconButton size="small" aria-label="Previous month" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
      <ChevronLeftIcon sx={{ fontSize: 20, color: AH_TEXT }} />
    </IconButton>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT }}>{format(date, "MMMM yyyy")}</Typography>
    <IconButton size="small" aria-label="Next month" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
      <ChevronRightIcon sx={{ fontSize: 20, color: AH_TEXT }} />
    </IconButton>
  </Box>
);

const DueDateField = ({
  id,
  value,
  onChange,
  fullWidth = false,
  showLabel = true,
  size,
  maxDate,
  markedDate,
  markedDateLabel = "Assignment due date",
  disabled = false,
  portalId,
}) => {
  const marked = toDate(markedDate);
  const isMarked = (date) => Boolean(marked) && isSameDay(date, marked);

  return (
  <Box className="audit-hub-due-date" sx={fullWidth ? { width: "100%" } : { maxWidth: { sm: 280 } }}>
    <DatePicker
      id={id}
      selected={toDate(value)}
      onChange={(date) => onChange(date ? format(date, "yyyy-MM-dd") : "")}
      minDate={new Date()}
      maxDate={toDate(maxDate) || undefined}
      disabled={disabled}
      portalId={portalId}
      dayClassName={(date) => (isMarked(date) ? "audit-hub-due-date-marked" : undefined)}
      renderDayContents={(day, date) =>
        isMarked(date) ? <span title={markedDateLabel} aria-label={markedDateLabel}>{day}</span> : day
      }
      dateFormat="MM/dd/yyyy"
      placeholderText="mm/dd/yyyy"
      showPopperArrow={false}
      popperPlacement="bottom-start"
      popperProps={{
        middleware: [],
      }}
      popperClassName="audit-hub-due-date-popper"
      calendarClassName="audit-hub-due-date-calendar"
      renderCustomHeader={(props) => <CalendarHeader {...props} />}
      customInput={
        <DueDateInput onClear={() => onChange("")} label={showLabel ? "Due Date" : ""} size={size} disabled={disabled} />
      }
    />
  </Box>
  );
};

export default DueDateField;
